/**
 * ChainVault WP - Earn Page (Tap-to-Earn)
 * TapSwap-style coin tapping game with upgrades and daily boosters.
 *
 * @package ChainVault
 * @since   1.0.0
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWallet } from '../context/WalletContext';
import toast from 'react-hot-toast';

const API = window.chainvaultSettings?.siteUrl || '/wp-json/chainvault/v1';

export default function EarnPage() {
    const { address, builtInWallet, walletType } = useWallet();
    const userAddress = walletType === 'builtin' && builtInWallet
        ? ( builtInWallet.solana?.address || builtInWallet.ethereum?.address )
        : address;

    const [ state,     setState     ] = useState( null );
    const [ loading,   setLoading   ] = useState( true );
    const [ tapping,   setTapping   ] = useState( false );
    const [ tab,       setTab       ] = useState( 'tap' ); /* tap | boost | upgrade */
    const [ floats,    setFloats    ] = useState( [] );
    const [ guruActive,setGuruActive] = useState( false );
    const [ refCode,   setRefCode   ] = useState( null );
    const [ refLoading,setRefLoading] = useState( false );
    const [ cipherInput, setCipherInput ] = useState( '' );
    const [ allocation, setAllocation ] = useState( null );
    const tapQueueRef  = useRef( 0 );
    const syncTimerRef = useRef( null );
    const guruTimerRef = useRef( null );
    const energyTimerRef = useRef( null );

    const fetchState = useCallback( async () => {
        if ( ! userAddress ) return;
        try {
            const res  = await fetch( API + '/earn/state?address=' + userAddress );
            const data = await res.json();
            setState( data );
        } catch(e) { console.error( 'Earn state error:', e ); }
        finally { setLoading( false ); }
    }, [ userAddress ] );

    useEffect( () => { fetchState(); }, [ fetchState ] );

    /* Once we know the mining campaign has ended (from the regular state
     * fetch), separately fetch this player's own token allocation —
     * computed fresh by the backend, see earn_my_allocation(). */
    useEffect( () => {
        if ( ! userAddress || ! state?.mining_has_ended ) return;
        fetch( API + '/earn/my-allocation?address=' + userAddress )
            .then( r => r.json() )
            .then( data => setAllocation( data ) )
            .catch( () => {} );
    }, [ userAddress, state?.mining_has_ended ] );

    /* Fetch (or create) this player's OWN referral code, so we can show
     * their invite link right inside the Earn tab. This calls the exact
     * same /referral/code endpoint used on the main Referral page — we are
     * not creating a second code system, just displaying the existing one
     * here too for convenience. */
    useEffect( () => {
        if ( ! userAddress ) return;
        setRefLoading( true );
        fetch( API + '/referral/code', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ address: userAddress }),
        })
            .then( r => r.json() )
            .then( data => setRefCode( data.code ) )
            .catch( () => {} )
            .finally( () => setRefLoading( false ) );
    }, [ userAddress ] );

    /*
     * EARN-GAME REFERRAL LINKING (one-time, runs once per browser/player).
     *
     * If this person arrived via someone else's ?ref=CODE link, App.js
     * already saved that code into localStorage as 'cv_referral_code'.
     * The very first time they open the Earn page, we send that code to
     * the backend so it can permanently remember who referred them — after
     * that, a small % of every coin they tap is paid to their referrer as
     * a bonus (see PHP comments in earn_tap() / earn_link_referrer() for
     * the full explanation). This is completely separate from the
     * platform-fee referral system on the main Referral page.
     */
    useEffect( () => {
        const refCode = localStorage.getItem( 'cv_referral_code' );
        if ( ! refCode || ! userAddress ) return;

        fetch( API + '/earn/link-referrer', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ address: userAddress, code: refCode }),
        }).catch( () => { /* silently ignore — non-critical, e.g. already linked */ } );
    }, [ userAddress ] );

    /* Energy regen timer */
    useEffect( () => {
        if ( ! state ) return;
        energyTimerRef.current = setInterval( () => {
            setState( prev => {
                if ( ! prev ) return prev;
                const regen = ( prev.energy_regen || 3 ) * ( prev.speed_level || 1 );
                const newEnergy = Math.min( prev.energy_max || 1000, ( prev.energy || 0 ) + regen );
                return { ...prev, energy: newEnergy };
            });
        }, 1000 );
        return () => clearInterval( energyTimerRef.current );
    }, [ state?.energy_max ] );

    /* Sync taps to server every 2s */
    const syncTaps = useCallback( async () => {
        const taps = tapQueueRef.current;
        if ( taps === 0 || ! userAddress ) return;
        tapQueueRef.current = 0;
        try {
            const res  = await fetch( API + '/earn/tap', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ address: userAddress, taps }),
            });
            const data = await res.json();
            if ( data.coins !== undefined ) {
                setState( prev => ({ ...prev, coins: data.coins, energy: data.energy }) );
            }
        } catch(e) { console.error( 'Tap sync error:', e ); }
    }, [ userAddress ] );

    useEffect( () => {
        syncTimerRef.current = setInterval( syncTaps, 2000 );
        return () => clearInterval( syncTimerRef.current );
    }, [ syncTaps ] );

    const handleTap = useCallback( ( e ) => {
        if ( ! state ) return;
        const multitap = state.multitap || 1;
        const energyCost = multitap;
        if ( ( state.energy || 0 ) < energyCost ) {
            toast.error( 'No energy! Wait for it to refill.', { id: 'energy' } );
            return;
        }

        /* Optimistic update */
        const earned = ( state.tap_reward || 1 ) * multitap;
        setState( prev => ({
            ...prev,
            coins:  ( prev.coins  || 0 ) + earned,
            energy: Math.max( 0, ( prev.energy || 0 ) - energyCost ),
        }));

        tapQueueRef.current += 1;

        /* Floating +N animation */
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const id = Date.now() + Math.random();
        setFloats( prev => [ ...prev, { id, x, y, value: '+' + earned } ] );
        setTimeout( () => setFloats( prev => prev.filter( f => f.id !== id ) ), 800 );

        setTapping( true );
        setTimeout( () => setTapping( false ), 100 );
    }, [ state ] );

    const handleUpgrade = async ( upgrade ) => {
        if ( ! userAddress ) return;
        try {
            const res  = await fetch( API + '/earn/upgrade', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ address: userAddress, upgrade }),
            });
            const data = await res.json();
            if ( data.success ) {
                setState( data.state );
                toast.success( 'Upgrade purchased!' );
            } else {
                toast.error( data.message || 'Not enough coins' );
            }
        } catch(e) { toast.error( 'Upgrade failed' ); }
    };

    /*
     * Handles a PAID upgrade purchase — builds and sends a real Solana
     * payment to the admin's configured payout address, then asks the
     * backend to independently verify that payment on-chain before
     * granting the upgrade. The backend never trusts anything claimed
     * here; this just kicks off the transaction and reports the result.
     */
    const handlePaidUpgrade = async ( upgradeKey, priceSOL, payoutAddress ) => {
        if ( ! builtInWallet?.solana?.secretKey ) {
            toast.error( 'A built-in Solana wallet is required to pay for upgrades.' );
            return;
        }
        try {
            const web3 = await import( '@solana/web3.js' );
            const isTestnet = localStorage.getItem( 'chainvault_testnet_mode' ) === '1';
            const rpc = isTestnet ? 'https://api.devnet.solana.com' : 'https://api.mainnet-beta.solana.com';
            const connection = new web3.Connection( rpc, 'confirmed' );
            const payer = web3.Keypair.fromSecretKey( new Uint8Array( builtInWallet.solana.secretKey ) );

            const tx = new web3.Transaction().add( web3.SystemProgram.transfer({
                fromPubkey: payer.publicKey,
                toPubkey:   new web3.PublicKey( payoutAddress ),
                lamports:   Math.floor( priceSOL * web3.LAMPORTS_PER_SOL ),
            }));
            const { blockhash } = await connection.getLatestBlockhash();
            tx.recentBlockhash = blockhash;
            tx.feePayer = payer.publicKey;
            tx.sign( payer );

            toast.loading( 'Sending payment...', { id: 'paid-upgrade' } );
            const sig = await connection.sendRawTransaction( tx.serialize() );
            await connection.confirmTransaction( sig, 'confirmed' );

            toast.loading( 'Verifying payment...', { id: 'paid-upgrade' } );
            const res  = await fetch( API + '/earn/verify-payment', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ address: userAddress, upgrade: upgradeKey, signature: sig, network: 'solana' }),
            });
            const data = await res.json();

            if ( data.success ) {
                setState( data.state );
                toast.success( 'Upgrade purchased!', { id: 'paid-upgrade' } );
            } else {
                toast.error( data.message || 'Payment verification failed', { id: 'paid-upgrade' } );
            }
        } catch( err ) {
            toast.error( err.message?.slice(0,80) || 'Payment failed', { id: 'paid-upgrade' } );
        }
    };

    /* Claims any pending Auto-Tap Bot passive coins, shown via the
     * "Claim X coins" popup that appears when state.pending_passive_coins > 0. */
    const handleClaimPassive = async () => {
        if ( ! userAddress ) return;
        try {
            const res  = await fetch( API + '/earn/claim-passive', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ address: userAddress }),
            });
            const data = await res.json();
            if ( data.success ) {
                setState( data.state );
                toast.success( 'Claimed ' + formatCoins( data.claimed ) + ' ' + coinSymbol + '!' );
            }
        } catch(e) { toast.error( 'Failed to claim' ); }
    };

    /* Claims today's daily login streak reward, shown via the
     * "Day X Streak!" popup that appears when state.pending_streak_coins > 0. */
    const handleClaimStreak = async () => {
        if ( ! userAddress ) return;
        try {
            const res  = await fetch( API + '/earn/claim-streak', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ address: userAddress }),
            });
            const data = await res.json();
            if ( data.success ) {
                setState( data.state );
                toast.success( 'Claimed ' + formatCoins( data.claimed ) + ' ' + coinSymbol + '!' );
            }
        } catch(e) { toast.error( 'Failed to claim' ); }
    };

    const handleClaimTask = async ( taskId ) => {
        if ( ! userAddress ) return;
        try {
            const res  = await fetch( API + '/earn/claim-task', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ address: userAddress, task_id: taskId }),
            });
            const data = await res.json();
            if ( data.success ) {
                setState( data.state );
                toast.success( 'Claimed ' + formatCoins( data.reward ) + ' ' + coinSymbol + '!' );
            } else {
                toast.error( data.message || 'Could not claim task' );
            }
        } catch(e) { toast.error( 'Failed to claim task' ); }
    };

    const handleClaimCipher = async () => {
        if ( ! userAddress || ! cipherInput.trim() ) return;
        try {
            const res  = await fetch( API + '/earn/claim-cipher', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ address: userAddress, code: cipherInput.trim() }),
            });
            const data = await res.json();
            if ( data.success ) {
                setState( data.state );
                setCipherInput( '' );
                toast.success( 'Cipher solved! +' + formatCoins( data.reward ) + ' ' + coinSymbol );
            } else {
                toast.error( data.message || 'Incorrect code' );
            }
        } catch(e) { toast.error( 'Failed to submit code' ); }
    };

    const handleDailyBoost = async ( boost ) => {
        if ( ! userAddress ) return;
        try {
            const res  = await fetch( API + '/earn/daily', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ address: userAddress, boost }),
            });
            const data = await res.json();
            if ( data.success ) {
                setState( data.state );
                if ( boost === 'tapping_guru' ) {
                    setGuruActive( true );
                    toast.success( 'Tapping Guru active for 20 seconds!' );
                    guruTimerRef.current = setTimeout( () => {
                        setGuruActive( false );
                        fetchState();
                    }, 20000 );
                } else {
                    toast.success( 'Energy fully restored!' );
                }
            } else {
                toast.error( data.message || 'No boosts left' );
            }
        } catch(e) { toast.error( 'Boost failed' ); }
    };

    const formatCoins = ( n ) => {
        if ( n >= 1000000 ) return ( n / 1000000 ).toFixed(1) + 'M';
        if ( n >= 1000    ) return ( n / 1000    ).toFixed(1) + 'K';
        return Math.floor( n ).toLocaleString();
    };

    const upgradeCosts = {
        multitap:     [ 2000, 5000, 12000, 25000, 50000 ],
        energy_limit: [ 3000, 7000, 15000, 30000, 60000 ],
        speed:        [ 1500, 4000, 10000, 20000, 40000 ],
    };

    if ( ! userAddress ) return (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', color:'#888', textAlign:'center', padding:'40px' }}>
            <div style={{ fontSize:'48px', marginBottom:'16px' }}>🪙</div>
            <h2 style={{ color:'#fff', marginBottom:'8px' }}>Connect Your Wallet</h2>
            <p>Connect your wallet to start earning coins by tapping.</p>
        </div>
    );

    if ( loading ) return (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh', color:'#888' }}>
            Loading...
        </div>
    );

    const coinName   = state?.coin_name   || 'Gold';
    const coinSymbol = state?.coin_symbol  || 'GOLD';
    const coins      = state?.coins        || 0;
    const energy     = Math.floor( state?.energy     || 0 );
    const energyMax  = state?.energy_max   || 1000;
    const multitap   = state?.multitap     || 1;
    const energyLvl  = state?.energy_level || 1;
    const speedLvl   = state?.speed_level  || 1;
    const dailyTaps  = state?.daily_taps   ?? 3;
    const dailyEnergy= state?.daily_energy ?? 3;

    const energyPct  = Math.min( 100, ( energy / energyMax ) * 100 );

    /*
     * MINING ENDED — show the claim/withdraw screen instead of the tap
     * game. Tapping no longer earns anything real once the campaign is
     * over, so we replace the whole page with a clear payout summary
     * instead of leaving the (now pointless) tap UI showing.
     */
    if ( state?.mining_has_ended ) {
        return (
            <div style={{ maxWidth:'480px', margin:'0 auto', padding:'16px', minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center' }}>
                <div style={{ fontSize:'56px', marginBottom:'16px' }}>⛏️</div>
                <h1 style={{ color:'#fff', fontSize:'22px', marginBottom:'8px' }}>Mining Period Ended</h1>
                <p style={{ color:'#888', fontSize:'13px', marginBottom:'28px' }}>Thanks for playing! Here is your final allocation.</p>

                { ! allocation ? (
                    <p style={{ color:'#666' }}>Loading your allocation...</p>
                ) : (
                    <>
                        <div style={{
                            width:'100%', background:'rgba(124,99,255,0.1)', border:'1px solid rgba(124,99,255,0.3)',
                            borderRadius:'18px', padding:'28px 20px', marginBottom:'20px',
                        }}>
                            <div style={{ fontSize:'12px', color:'#aaa', marginBottom:'8px' }}>Lifetime { coinSymbol } earned</div>
                            <div style={{ fontSize:'20px', fontWeight:'700', color:'#fff', marginBottom:'20px' }}>
                                { formatCoins( allocation.lifetime_coins || 0 ) }
                            </div>
                            <div style={{ fontSize:'12px', color:'#aaa', marginBottom:'8px' }}>Your token allocation</div>
                            <div style={{ fontSize:'32px', fontWeight:'900', color:'#7C63FF' }}>
                                { ( allocation.token_allocation || 0 ).toLocaleString( undefined, { maximumFractionDigits: 6 } ) }
                            </div>
                        </div>

                        { allocation.already_paid ? (
                            <div style={{ width:'100%', padding:'14px', borderRadius:'12px', background:'rgba(0,200,120,0.15)', border:'1px solid rgba(0,200,120,0.3)', color:'#22c55e', fontWeight:'700', fontSize:'14px' }}>
                                ✅ Payment sent to your wallet
                                { allocation.payout_tx && (
                                    <div style={{ marginTop:'8px', fontSize:'11px', color:'#888', wordBreak:'break-all' }}>{ allocation.payout_tx }</div>
                                )}
                            </div>
                        ) : (
                            <div style={{ width:'100%', padding:'14px', borderRadius:'12px', background:'rgba(255,165,0,0.1)', border:'1px solid rgba(255,165,0,0.3)', color:'#FFA500', fontWeight:'600', fontSize:'13px' }}>
                                ⏳ Payment pending — the project team will send this to your wallet address soon.
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }

    return (
        <div style={{ maxWidth:'480px', margin:'0 auto', padding:'16px', minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center' }}>

            {/* CLAIM POPUPS — daily streak takes priority (shown first, since
                it's the daily habit hook), then passive income afterwards,
                so the player never sees two popups stacked on top of each
                other. Each disappears once claimed, revealing the next one
                underneath if there is one. */}
            { ( state?.pending_streak_coins || 0 ) > 0 ? (
                <div style={{
                    position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000,
                    display:'flex', alignItems:'center', justifyContent:'center', padding:'20px',
                }}>
                    <div style={{
                        background:'linear-gradient(135deg,#1a1a2e,#16162a)', borderRadius:'20px',
                        padding:'32px 24px', maxWidth:'320px', width:'100%', textAlign:'center',
                        border:'1px solid rgba(255,165,0,0.3)', boxShadow:'0 20px 60px rgba(0,0,0,0.5)',
                    }}>
                        <div style={{ fontSize:'56px', marginBottom:'12px' }}>🔥</div>
                        <h2 style={{ color:'#fff', fontSize:'20px', marginBottom:'8px' }}>Day { state.streak_count_pending || state.streak_count } Streak!</h2>
                        <p style={{ color:'#aaa', fontSize:'13px', marginBottom:'20px' }}>Come back tomorrow to keep your streak alive</p>
                        <div style={{ fontSize:'32px', fontWeight:'900', color:'#FFA500', marginBottom:'24px' }}>
                            +{ formatCoins( state.pending_streak_coins ) } { coinSymbol }
                        </div>
                        <button onClick={ handleClaimStreak } style={{
                            width:'100%', padding:'14px', border:'none', borderRadius:'12px', cursor:'pointer',
                            background:'linear-gradient(135deg,#FFA500,#FF6B00)', color:'#fff', fontWeight:'700', fontSize:'15px',
                        }}>
                            Claim
                        </button>
                    </div>
                </div>
            ) : ( ( state?.pending_passive_coins || 0 ) > 0 && (
                <div style={{
                    position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000,
                    display:'flex', alignItems:'center', justifyContent:'center', padding:'20px',
                }}>
                    <div style={{
                        background:'linear-gradient(135deg,#1a1a2e,#16162a)', borderRadius:'20px',
                        padding:'32px 24px', maxWidth:'320px', width:'100%', textAlign:'center',
                        border:'1px solid rgba(124,99,255,0.3)', boxShadow:'0 20px 60px rgba(0,0,0,0.5)',
                    }}>
                        <div style={{ fontSize:'56px', marginBottom:'12px' }}>🤖</div>
                        <h2 style={{ color:'#fff', fontSize:'20px', marginBottom:'8px' }}>Welcome back!</h2>
                        <p style={{ color:'#aaa', fontSize:'13px', marginBottom:'20px' }}>Your Auto-Tap Bot earned coins while you were away</p>
                        <div style={{ fontSize:'32px', fontWeight:'900', color:'#7C63FF', marginBottom:'24px' }}>
                            +{ formatCoins( state.pending_passive_coins ) } { coinSymbol }
                        </div>
                        <button onClick={ handleClaimPassive } style={{
                            width:'100%', padding:'14px', border:'none', borderRadius:'12px', cursor:'pointer',
                            background:'linear-gradient(135deg,#7C63FF,#FC72FF)', color:'#fff', fontWeight:'700', fontSize:'15px',
                        }}>
                            Claim
                        </button>
                    </div>
                </div>
            ))}

            {/* League badge — based on LIFETIME coins earned, never demotes
                even when the player spends coins on upgrades. */}
            { state?.league && (
                <div style={{
                    width:'100%', marginBottom:'16px', padding:'14px 16px', borderRadius:'14px',
                    background:'linear-gradient(135deg, rgba(124,99,255,0.15), rgba(252,114,255,0.1))',
                    border:'1px solid rgba(124,99,255,0.25)',
                }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: state.next_league ? '10px' : '0' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                            <span style={{ fontSize:'22px' }}>{ state.league.icon }</span>
                            <span style={{ color:'#fff', fontWeight:'800', fontSize:'15px' }}>{ state.league.name }</span>
                        </div>
                        <span style={{ color:'#aaa', fontSize:'12px' }}>{ formatCoins( state.lifetime_coins_earned || 0 ) } lifetime</span>
                    </div>
                    { state.next_league && (
                        <>
                            <div style={{ width:'100%', height:'6px', background:'rgba(255,255,255,0.1)', borderRadius:'3px', overflow:'hidden' }}>
                                <div style={{
                                    height:'100%', borderRadius:'3px',
                                    width: Math.min( 100, ( ( ( state.lifetime_coins_earned || 0 ) - state.league.threshold ) / ( state.next_league.threshold - state.league.threshold ) ) * 100 ) + '%',
                                    background:'linear-gradient(90deg,#7C63FF,#FC72FF)',
                                }} />
                            </div>
                            <div style={{ fontSize:'11px', color:'#888', marginTop:'6px' }}>
                                { formatCoins( state.next_league.threshold - ( state.lifetime_coins_earned || 0 ) ) } { coinSymbol } to { state.next_league.icon } { state.next_league.name }
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Stats bar */}
            <div style={{ display:'flex', gap:'12px', width:'100%', marginBottom:'20px', flexWrap:'wrap' }}>
                { [
                    { label: coinSymbol, value: formatCoins( coins ), icon: '🪙' },
                    { label: 'Multitap', value: 'x' + multitap,       icon: '👆' },
                    { label: 'Energy',   value: energy + '/' + energyMax, icon: '⚡' },
                ].map( ( s, i ) => (
                    <div key={i} style={{ flex:1, minWidth:'80px', background:'rgba(255,255,255,0.05)', borderRadius:'12px', padding:'10px', textAlign:'center' }}>
                        <div style={{ fontSize:'18px' }}>{ s.icon }</div>
                        <div style={{ fontSize:'13px', fontWeight:'700', color:'#fff' }}>{ s.value }</div>
                        <div style={{ fontSize:'10px', color:'#666' }}>{ s.label }</div>
                    </div>
                ))}
            </div>

            {/* Tab nav */}
            <div style={{ display:'flex', gap:'8px', marginBottom:'24px', background:'rgba(255,255,255,0.05)', borderRadius:'12px', padding:'4px', width:'100%' }}>
                { [ ['tap','🪙 Tap'], ['boost','⚡ Boost'], ['upgrade','🔧 Upgrade'], ['quests','📋 Quests'], ['invite','🤝 Invite'] ].map( ([key,label]) => (
                    <button key={key} onClick={()=>setTab(key)} style={{
                        flex:1, padding:'8px', border:'none', borderRadius:'8px', cursor:'pointer',
                        background: tab===key ? 'linear-gradient(135deg,#7C63FF,#FC72FF)' : 'transparent',
                        color: tab===key ? '#fff' : '#888', fontWeight:'700', fontSize:'13px',
                    }}>{ label }</button>
                ))}
            </div>

            {/* TAP TAB */}
            { tab === 'tap' && (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:'100%' }}>
                    <div style={{ fontSize:'32px', fontWeight:'900', color:'#fff', marginBottom:'4px' }}>
                        { formatCoins( coins ) } { coinSymbol }
                    </div>
                    <div style={{ fontSize:'13px', color:'#888', marginBottom:'32px' }}>Tap the coin to earn { coinName }</div>

                    {/* Big tappable coin — outer wrapper handles the slow idle "breathing"
                        pulse via CSS animation, inner coin handles the instant tap-press
                        scale via inline style. Splitting these into two elements avoids
                        the two transforms fighting each other. */}
                    <div
                        onClick={ handleTap }
                        style={{
                            position:'relative', width:'220px', height:'220px',
                            cursor:'pointer', userSelect:'none',
                            display:'flex', alignItems:'center', justifyContent:'center',
                            marginBottom:'32px',
                            animation: guruActive ? 'earnBreathe 0.8s ease-in-out infinite' : 'earnBreathe 2.4s ease-in-out infinite',
                        }}
                    >
                        <div style={{
                            position:'absolute', inset:0, borderRadius:'50%',
                            background: state?.coin_image
                                ? 'transparent'
                                : ( guruActive
                                    ? 'radial-gradient(circle at 35% 35%, #FFD700, #FF8C00, #FF4500)'
                                    : 'radial-gradient(circle at 35% 35%, #FFD700, #FFA500, #FF8C00)' ),
                            boxShadow: state?.coin_image
                                ? 'none'
                                : ( guruActive
                                    ? '0 0 60px rgba(255,100,0,0.8), 0 0 120px rgba(255,200,0,0.4)'
                                    : '0 0 40px rgba(255,165,0,0.4), 0 8px 32px rgba(0,0,0,0.4)' ),
                            transform: tapping ? 'scale(0.92)' : 'scale(1)',
                            transition:'transform 0.1s ease',
                            display:'flex', alignItems:'center', justifyContent:'center',
                            fontSize:'80px', overflow:'hidden',
                        }}>
                            { state?.coin_image
                                ? <img src={ state.coin_image } alt={ coinName } style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%', filter: guruActive ? 'drop-shadow(0 0 30px rgba(255,100,0,0.8))' : 'drop-shadow(0 0 20px rgba(255,165,0,0.4))' }} />
                                : '🪙'
                            }
                        </div>
                        { floats.map( f => (
                            <div key={f.id} style={{
                                position:'absolute', left: f.x, top: f.y,
                                color:'#FFD700', fontWeight:'900', fontSize:'22px',
                                pointerEvents:'none', animation:'earnFloat 0.8s ease-out forwards',
                                textShadow:'0 2px 8px rgba(0,0,0,0.6)',
                                zIndex: 10,
                            }}>{ f.value }</div>
                        ))}
                    </div>

                    {/* Energy bar */}
                    <div style={{ width:'100%', marginBottom:'8px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px', fontSize:'13px', color:'#888' }}>
                            <span>⚡ Energy</span>
                            <span style={{ color:'#fff' }}>{ energy } / { energyMax }</span>
                        </div>
                        <div style={{ width:'100%', height:'12px', background:'rgba(255,255,255,0.1)', borderRadius:'6px', overflow:'hidden' }}>
                            <div style={{
                                height:'100%', borderRadius:'6px',
                                width: energyPct + '%',
                                background: energyPct > 50 ? 'linear-gradient(90deg,#7C63FF,#00e5a0)' : energyPct > 20 ? 'linear-gradient(90deg,#F0B90B,#FF8C00)' : 'linear-gradient(90deg,#ff5577,#ff8c00)',
                                transition:'width 0.3s ease',
                            }} />
                        </div>
                    </div>
                    { guruActive && (
                        <div style={{ marginTop:'12px', padding:'8px 16px', background:'rgba(255,100,0,0.2)', borderRadius:'8px', color:'#FFD700', fontSize:'13px', fontWeight:'700' }}>
                            🔥 Tapping Guru Active!
                        </div>
                    )}
                </div>
            )}

            {/* BOOST TAB */}
            { tab === 'boost' && (
                <div style={{ width:'100%' }}>
                    <p style={{ color:'#888', fontSize:'13px', marginBottom:'20px', textAlign:'center' }}>Daily boosts reset every 24 hours</p>
                    { [
                        { key:'tapping_guru', icon:'🔥', name:'Tapping Guru', desc:'5x tap power for 20 seconds', left: dailyTaps },
                        { key:'full_tank',    icon:'⚡', name:'Full Tank',    desc:'Instantly refill your energy', left: dailyEnergy },
                    ].map( b => (
                        <div key={b.key} onClick={() => handleDailyBoost(b.key)} style={{
                            display:'flex', alignItems:'center', gap:'16px', padding:'16px',
                            background:'rgba(255,255,255,0.05)', borderRadius:'14px', marginBottom:'12px',
                            cursor: b.left > 0 ? 'pointer' : 'not-allowed', opacity: b.left > 0 ? 1 : 0.4,
                            border:'1px solid rgba(255,255,255,0.08)',
                        }}>
                            <div style={{ fontSize:'36px' }}>{ b.icon }</div>
                            <div style={{ flex:1 }}>
                                <div style={{ color:'#fff', fontWeight:'700', fontSize:'15px' }}>{ b.name }</div>
                                <div style={{ color:'#888', fontSize:'12px' }}>{ b.desc }</div>
                            </div>
                            <div style={{ background:'rgba(124,99,255,0.2)', borderRadius:'8px', padding:'4px 10px', color:'#7C63FF', fontWeight:'700', fontSize:'13px' }}>
                                { b.left }/3
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* UPGRADE TAB */}
            { tab === 'upgrade' && (
                <div style={{ width:'100%' }}>
                    <p style={{ color:'#888', fontSize:'13px', marginBottom:'20px', textAlign:'center' }}>Spend { coinSymbol } to permanently upgrade</p>
                    { [
                        { key:'multitap',     icon:'👆', name:'Multitap',     desc:'Earn more per tap',                level: multitap,                          maxLevel: 5 },
                        { key:'energy_limit', icon:'🔋', name:'Energy Limit', desc:'Increase max energy',             level: energyLvl,                         maxLevel: 5 },
                        { key:'speed',        icon:'⚡', name:'Regen Speed',  desc:'Energy recharges faster',         level: speedLvl,                          maxLevel: 5 },
                        { key:'auto_tap_bot', icon:'🤖', name:'Auto-Tap Bot', desc:'Earn coins automatically while away', level: state?.auto_tap_bot_level || 0, maxLevel: 5 },
                    ].map( u => {
                        const paidConfig  = state?.paid_upgrades?.[ u.key ];
                        const isPaidMode  = paidConfig?.enabled && paidConfig?.solana_address;
                        const maxed       = u.level >= u.maxLevel;

                        /* Free mode: cost in in-game coins, taken from the original upgradeCosts table.
                         * Auto-Tap Bot has no free-mode cost table since it's a brand-new upgrade meant
                         * to be configured as paid — if an admin leaves it free, default to a flat
                         * coin cost per level so it's still usable. */
                        const freeCostTable = upgradeCosts[ u.key ] || [ 10000, 25000, 50000, 100000, 200000 ];
                        const freeCost      = freeCostTable[ u.level ];
                        const canAffordFree = coins >= freeCost;

                        /* Paid mode: price comes from admin-configured comma-separated SOL prices */
                        const solPrices = paidConfig?.solana_prices ? paidConfig.solana_prices.split(',').map( p => parseFloat( p.trim() ) ) : [];
                        const solPrice  = solPrices[ u.level ] || 0;

                        return (
                            <div key={u.key} style={{
                                display:'flex', alignItems:'center', gap:'16px', padding:'16px',
                                background:'rgba(255,255,255,0.05)', borderRadius:'14px', marginBottom:'12px',
                                border:'1px solid rgba(255,255,255,0.08)',
                            }}>
                                <div style={{ fontSize:'32px' }}>{ u.icon }</div>
                                <div style={{ flex:1 }}>
                                    <div style={{ color:'#fff', fontWeight:'700', fontSize:'15px' }}>{ u.name }</div>
                                    <div style={{ color:'#888', fontSize:'12px' }}>{ u.desc }</div>
                                    <div style={{ display:'flex', gap:'4px', marginTop:'6px' }}>
                                        { Array.from({length:5}).map((_,i)=>(
                                            <div key={i} style={{ width:'16px', height:'4px', borderRadius:'2px', background: i < u.level ? '#7C63FF' : 'rgba(255,255,255,0.1)' }} />
                                        ))}
                                    </div>
                                </div>
                                { isPaidMode ? (
                                    <button onClick={() => !maxed && solPrice > 0 && handlePaidUpgrade( u.key, solPrice, paidConfig.solana_address )} style={{
                                        padding:'8px 14px', border:'none', borderRadius:'10px', cursor: maxed || solPrice <= 0 ? 'not-allowed' : 'pointer',
                                        background: maxed ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg,#14F195,#9945FF)',
                                        color: maxed ? '#666' : '#fff', fontWeight:'700', fontSize:'12px', whiteSpace:'nowrap',
                                    }}>
                                        { maxed ? 'MAX' : solPrice + ' SOL' }
                                    </button>
                                ) : (
                                    <button onClick={() => !maxed && handleUpgrade(u.key)} style={{
                                        padding:'8px 14px', border:'none', borderRadius:'10px', cursor: maxed || !canAffordFree ? 'not-allowed' : 'pointer',
                                        background: maxed ? 'rgba(255,255,255,0.1)' : canAffordFree ? 'linear-gradient(135deg,#7C63FF,#FC72FF)' : 'rgba(255,255,255,0.08)',
                                        color: maxed || !canAffordFree ? '#666' : '#fff', fontWeight:'700', fontSize:'12px', whiteSpace:'nowrap',
                                    }}>
                                        { maxed ? 'MAX' : formatCoins(freeCost) + ' ' + coinSymbol }
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* QUESTS TAB */}
            { tab === 'quests' && (
                <div style={{ width:'100%' }}>
                    { ( state?.cipher_reward || 0 ) > 0 && ! state?.cipher_claimed_today && (
                        <div style={{
                            background:'linear-gradient(135deg, rgba(255,165,0,0.15), rgba(255,69,0,0.1))',
                            border:'1px solid rgba(255,165,0,0.3)', borderRadius:'14px', padding:'16px', marginBottom:'16px',
                        }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}>
                                <span style={{ fontSize:'20px' }}>🔐</span>
                                <span style={{ color:'#fff', fontWeight:'800', fontSize:'14px' }}>Daily Cipher</span>
                                <span style={{ marginLeft:'auto', color:'#FFA500', fontWeight:'700', fontSize:'13px' }}>+{ formatCoins( state.cipher_reward ) } { coinSymbol }</span>
                            </div>
                            <p style={{ fontSize:'12px', color:'#aaa', marginBottom:'10px' }}>Find today's secret code on our social channels and enter it below</p>
                            <div style={{ display:'flex', gap:'8px' }}>
                                <input
                                    value={ cipherInput }
                                    onChange={ e => setCipherInput( e.target.value ) }
                                    onKeyDown={ e => e.key === 'Enter' && handleClaimCipher() }
                                    placeholder="Enter code..."
                                    style={{ flex:1, padding:'10px 12px', borderRadius:'10px', border:'1px solid rgba(255,255,255,0.1)', background:'rgba(0,0,0,0.3)', color:'#fff', fontSize:'13px' }}
                                />
                                <button onClick={ handleClaimCipher } style={{
                                    padding:'10px 16px', border:'none', borderRadius:'10px', cursor:'pointer',
                                    background:'linear-gradient(135deg,#FFA500,#FF6B00)', color:'#fff', fontWeight:'700', fontSize:'13px', whiteSpace:'nowrap',
                                }}>Submit</button>
                            </div>
                        </div>
                    )}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
                        <p style={{ color:'#888', fontSize:'13px', margin:0 }}>Complete tasks to earn bonus coins</p>
                        <span style={{ fontSize:'11px', color:'#666' }}>
                            { formatCoins( state?.quest_coins_today || 0 ) } / { formatCoins( state?.quest_daily_cap || 5000 ) } today
                        </span>
                    </div>

                    { ( ! state?.quests || state.quests.length === 0 ) && (
                        <div style={{ textAlign:'center', color:'#666', padding:'40px 20px', fontSize:'13px' }}>
                            No tasks available right now. Check back soon!
                        </div>
                    )}

                    { ( state?.quests || [] ).map( ( q ) => {
                        const isOneTimeDone = ! q.repeatable && ( state?.completed_tasks || [] ).includes( q.id );
                        const isDailyDone   = q.repeatable && ( state?.completed_tasks_today || [] ).includes( q.id );
                        const isDone        = isOneTimeDone || isDailyDone;
                        const capReached    = ( ( state?.quest_coins_today || 0 ) + ( q.reward || 0 ) ) > ( state?.quest_daily_cap || 5000 );

                        return (
                            <div key={q.id} style={{
                                display:'flex', alignItems:'center', gap:'12px', padding:'14px 16px',
                                background:'rgba(255,255,255,0.05)', borderRadius:'14px', marginBottom:'10px',
                                border:'1px solid rgba(255,255,255,0.08)', opacity: isDone ? 0.5 : 1,
                            }}>
                                <div style={{ fontSize:'24px' }}>{ q.repeatable ? '🔁' : '✅' }</div>
                                <div style={{ flex:1 }}>
                                    <div style={{ color:'#fff', fontWeight:'700', fontSize:'14px' }}>{ q.title }</div>
                                    <div style={{ color:'#7C63FF', fontSize:'12px', fontWeight:'600' }}>+{ formatCoins( q.reward ) } { coinSymbol }</div>
                                </div>
                                { q.link && ! isDone && (
                                    <a href={ q.link } target="_blank" rel="noopener noreferrer" style={{
                                        padding:'8px 12px', borderRadius:'8px', background:'rgba(255,255,255,0.08)',
                                        color:'#aaa', fontSize:'12px', fontWeight:'600', textDecoration:'none', whiteSpace:'nowrap',
                                    }}>Open ↗</a>
                                )}
                                <button
                                    onClick={ () => ! isDone && ! capReached && handleClaimTask( q.id ) }
                                    disabled={ isDone || capReached }
                                    style={{
                                        padding:'8px 14px', border:'none', borderRadius:'10px', whiteSpace:'nowrap',
                                        cursor: isDone || capReached ? 'not-allowed' : 'pointer',
                                        background: isDone ? 'rgba(255,255,255,0.05)' : capReached ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg,#7C63FF,#FC72FF)',
                                        color: isDone || capReached ? '#666' : '#fff', fontWeight:'700', fontSize:'12px',
                                    }}
                                >
                                    { isDone ? 'Done' : capReached ? 'Cap reached' : 'Claim' }
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* INVITE TAB */}
            { tab === 'invite' && (
                <div style={{ width:'100%' }}>
                    <p style={{ color:'#888', fontSize:'13px', marginBottom:'20px', textAlign:'center' }}>
                        Invite friends and earn { state?.earn_referral_pct || 10 }% of every coin they tap, forever
                    </p>

                    <div style={{ display:'flex', gap:'12px', marginBottom:'16px' }}>
                        <div style={{ flex:1, background:'rgba(124,99,255,0.1)', border:'1px solid rgba(124,99,255,0.3)', borderRadius:'14px', padding:'20px', textAlign:'center' }}>
                            <div style={{ fontSize:'13px', color:'#aaa', marginBottom:'10px' }}>Friends referred</div>
                            <div style={{ fontSize:'28px', fontWeight:'900', color:'#fff' }}>
                                { state?.referred_count || 0 }
                            </div>
                        </div>
                        <div style={{ flex:1, background:'rgba(124,99,255,0.1)', border:'1px solid rgba(124,99,255,0.3)', borderRadius:'14px', padding:'20px', textAlign:'center' }}>
                            <div style={{ fontSize:'13px', color:'#aaa', marginBottom:'10px' }}>Coins earned from referrals</div>
                            <div style={{ fontSize:'28px', fontWeight:'900', color:'#7C63FF' }}>
                                { formatCoins( state?.referral_bonus_earned || 0 ) }
                            </div>
                        </div>
                    </div>

                    { state?.referred_players && state.referred_players.length > 0 && (
                        <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:'14px', padding:'16px', border:'1px solid rgba(255,255,255,0.08)', marginBottom:'16px' }}>
                            <div style={{ fontSize:'12px', color:'#888', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                                Your friends
                            </div>
                            { state.referred_players.slice().reverse().map( ( p, i ) => (
                                <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom: i < state.referred_players.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                                    <span style={{ fontFamily:'monospace', fontSize:'12px', color:'#ccc' }}>
                                        { p.address.slice(0,6) + '...' + p.address.slice(-4) }
                                    </span>
                                    <span style={{ fontSize:'11px', color:'#666' }}>
                                        { new Date( p.joined_at * 1000 ).toLocaleDateString() }
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:'14px', padding:'16px', border:'1px solid rgba(255,255,255,0.08)' }}>
                        <div style={{ fontSize:'12px', color:'#888', marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                            Your invite link
                        </div>
                        { refLoading ? (
                            <div style={{ color:'#666', fontSize:'13px', padding:'10px 0' }}>Loading your link...</div>
                        ) : refCode ? (
                            <div style={{ display:'flex', gap:'8px' }}>
                                <input
                                    readOnly
                                    value={ `${window.location.origin}${window.location.pathname}?ref=${refCode}` }
                                    onClick={ (e) => e.target.select() }
                                    style={{
                                        flex:1, padding:'10px 12px', borderRadius:'10px', border:'1px solid rgba(255,255,255,0.1)',
                                        background:'rgba(0,0,0,0.3)', color:'#fff', fontSize:'12px', fontFamily:'monospace',
                                    }}
                                />
                                <button
                                    onClick={ () => {
                                        navigator.clipboard.writeText( `${window.location.origin}${window.location.pathname}?ref=${refCode}` );
                                        toast.success( 'Link copied!' );
                                    }}
                                    style={{
                                        padding:'10px 16px', border:'none', borderRadius:'10px', cursor:'pointer',
                                        background:'linear-gradient(135deg,#7C63FF,#FC72FF)', color:'#fff', fontWeight:'700', fontSize:'13px', whiteSpace:'nowrap',
                                    }}
                                >Copy</button>
                            </div>
                        ) : (
                            <div style={{ color:'#666', fontSize:'13px', padding:'10px 0' }}>Connect a wallet to get your link.</div>
                        )}
                    </div>

                    <div style={{ marginTop:'16px', padding:'14px 16px', background:'rgba(255,255,255,0.03)', borderRadius:'12px', fontSize:'12px', color:'#777', lineHeight:'1.6' }}>
                        Share this link with friends. When they open it and play the Earn game, you'll automatically earn a % of every coin they tap — paid straight into your { coinSymbol } balance.
                    </div>
                </div>
            )}

            <style>{`
                @keyframes earnFloat {
                    0%   { opacity:1; transform: translateY(0) scale(1); }
                    100% { opacity:0; transform: translateY(-60px) scale(1.4); }
                }
                @keyframes earnBreathe {
                    0%, 100% { transform: scale(1); }
                    50%      { transform: scale(1.05); }
                }
            `}</style>
        </div>
    );
}
